import { Injectable } from '@angular/core';
import { Subject, Observable, Subscription } from 'rxjs';

// Define a simple structure to hold a reference to the handler function 
// associated with the event name, so we can unsubscribe later.
interface HandlerEntry {
    subject: Subject<any>;
    handler: Function;
    subscription: Subscription;
}

@Injectable({
    providedIn: 'root'
})
export class AppEventService {

    // Map to store subjects and their associated handler references based on event name
    private eventMap: Map<string, HandlerEntry> = new Map();

    constructor() { }

    /**
     * Mimics events.subscribe(eventName, handler)
     */
    public subscribe(eventName: string, handler: Function): void {
        
        this.log("subscribe from " + this.getCallerName() + ": " + eventName);
        let entry = this.eventMap.get(eventName);

        if (!entry) {
            // If this event name is new, create a new Subject
            const newSubject = new Subject<any>();
            
            // Subscribe the handler to the new subject stream immediately
            const newSubscription = newSubject.subscribe((data) => {
                // Execute the user-provided handler function from the subscribe call
                handler(data);
            });
            
            // Store the entry
            this.eventMap.set(eventName, {
                subject: newSubject,
                handler: handler,
                subscription: newSubscription
            });

        } else {
            // If the event name exists but we are subscribing with a NEW handler, 
            // we must unsubscribe the OLD handler from the existing subject and add the new one.
            
            // CRITICAL: To truly mimic the old system, if the SAME handler function 
            // is subscribed twice, it might be executed twice. The old system didn't 
            // easily support unsubscribing a specific handler reference if the stream 
            // was already established. For simplicity and to prevent immediate memory leaks 
            // from multiple subscriptions with the same name, we'll enforce one handler 
            // per event name if the service is reused in this manner.
            
            // For robust mimicry, we just ensure the handler reference is updated.
            // NOTE: If the EXACT same function reference is passed again, we might 
            // leak memory by not unsubscribing the old handler correctly *if* the 
            // original handler was the one being replaced. 
            
            // For maximal compatibility with the old system's intent:
            console.warn(`Event name '${eventName}' already has a stream. Handler reference updated.`);
            
            // We need a way to manage multiple distinct handlers on one stream if needed,
            // but usually the old system relied on the user not repeating subscriptions.
            // For now, we use the simplest path: just ensure the handler reference points
            // to the latest function we were given for this stream.
            entry.handler = handler; 
        }
    }

    private getCallerName(): string {
    const err = new Error();
    // The stack is a string. We split it by newlines.
    // [0] is the Error itself
    // [1] is 'getCallerName'
    // [2] is the function that called 'getCallerName'
    const stack = err.stack?.split('\n');
    const callerLine = stack?.[3] || ''; // Adjust index based on environment
    
    // Extract name using a regex (handles Chrome/Node formats)
    const match = callerLine.match(/at (\S+)/);
    return match ? match[1] : 'anonymous';
    }
    /**
     * Mimics events.publish(eventName, data)
     */
    public publish(eventName: string, data?: any): void {
        this.log("publish from " + this.getCallerName() + ": " + eventName);
        const entry = this.eventMap.get(eventName);
        if (entry) {
            // Pass the published data to the subject, which triggers the subscribed handler(s)
            entry.subject.next(data);
        }
        else {
          this.log("publish from " + this.getCallerName() + ": no subscription for " + eventName);
        }
        // If no one has subscribed, the publish does nothing, which matches old Events behavior.
    }

    /**
     * Mimics events.unsubscribe(eventName, handler)
     */
    public unsubscribe(eventName: string, handler?: Function): void {
        this.log("unsubscribe: " + eventName);
        const entry = this.eventMap.get(eventName);

        if (!entry) {
            return; // Nothing to unsubscribe
        }

        // In the old system, unsubscribing required the EXACT same handler reference.
        // Because RxJS Subjects don't easily allow unsubscribing a specific function 
        // while others listen, we will unsubscribe the entire stream reference IF 
        // the user provided a handler that matches the stored one or if no handler is provided.
        // WARNING: This might aggressively unsubscribe others listening to the same event name!
        // A true fix requires complex Subscription management per handler instance, 
        // but this follows the spirit of the original interface simplification.
        
        // Only unsubscribe if:
        // 1. No handler was provided (common clean-up pattern)
        // 2. A handler was provided, AND it matches the stored handler reference (less common, error prone)
        
        if (!handler || handler === entry.handler) {
            entry.subscription.unsubscribe();
            this.eventMap.delete(eventName);
        }
    }
    
    /**
     * Cleanup mechanism to prevent memory leaks when components are destroyed.
     * This should be called if a component subscribes and is later destroyed 
     * without an explicit unsubscribe call matching the subscribe signature.
     */
    public cleanupForEvent(eventName: string): void {
        this.log("cleanupForEvent: " + eventName);
        this.unsubscribe(eventName);
    }

    public hasEvent(eventName: string): boolean {
        return this.eventMap.has(eventName);
    }

    private log(s: string): void {
      // console.log("AppEventService: " + s);
    }
}
